"use client";

import { useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { createClient } from "@/lib/supabase/client";

export default function SignupPage() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const supabase = createClient();

  async function handleGoogleSignup() {
    setLoading(true);
    setError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) setError(error.message);
    setLoading(false);
  }

  async function handleEmailSignup(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      setError(error.message);
    } else {
      setSuccess(true);
    }
    setLoading(false);
  }

  if (success) {
    return (
      <div className="space-y-6 text-center">
        <div className="lg:hidden">
          <h1 className="text-2xl font-black text-foreground">
            Got<span className="text-brand">RedFlags</span>
          </h1>
        </div>
        <div className="rounded-lg bg-green-950/50 border border-green-900/50 p-6">
          <div className="text-3xl mb-3">&#9993;&#65039;</div>
          <h2 className="text-lg font-bold text-foreground">Check your email</h2>
          <p className="text-sm text-muted-foreground mt-2">
            We&apos;ve sent a verification link to{" "}
            <span className="text-foreground font-medium">{email}</span>. Click the
            link to activate your account.
          </p>
        </div>
        <Link href="/login" className="text-xs text-brand hover:text-brand/80">
          Back to login
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="lg:hidden text-center">
        <h1 className="text-2xl font-black text-foreground">
          Got<span className="text-brand">RedFlags</span>
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          Find them before you sign.
        </p>
      </div>

      <div>
        <h2 className="text-xl font-bold text-foreground">Create your account</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Start with 2 free contract scans + 1 free property check
        </p>
      </div>

      <Button
        variant="outline"
        className="w-full bg-white text-neutral-900 hover:bg-neutral-100 border-neutral-200 dark:bg-white dark:text-neutral-900 dark:hover:bg-neutral-100 dark:border-neutral-200"
        onClick={handleGoogleSignup}
        disabled={loading}
      >
        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
        Continue with Google
      </Button>

      <div className="relative">
        <Separator className="bg-border" />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-2 text-xs text-muted-foreground">
          or
        </span>
      </div>

      {error && (
        <div className="rounded-lg bg-destructive/10 border border-destructive/30 p-3 text-xs text-destructive">
          {error}
        </div>
      )}

      <form onSubmit={handleEmailSignup} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-foreground/80 text-sm">
            Full name
          </Label>
          <Input
            id="name"
            type="text"
            placeholder="Amith Suresh"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            className="bg-card border-border text-foreground placeholder:text-muted-foreground"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-foreground/80 text-sm">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="bg-card border-border text-foreground placeholder:text-muted-foreground"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-foreground/80 text-sm">
            Password
          </Label>
          <Input
            id="password"
            type="password"
            placeholder="At least 6 characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            className="bg-card border-border text-foreground placeholder:text-muted-foreground"
          />
        </div>
        <Button
          type="submit"
          className="w-full bg-brand hover:bg-brand/90 text-brand-foreground"
          disabled={loading}
        >
          {loading ? "Creating account..." : "Create account"}
        </Button>
      </form>

      <p className="text-center text-xs text-muted-foreground">
        Already have an account?{" "}
        <Link href="/login" className="text-brand hover:text-brand/80">
          Sign in
        </Link>
      </p>

      <p className="text-center text-[10px] text-muted-foreground/60">
        By signing up, you agree to our{" "}
        <Link href="/terms" className="underline hover:text-muted-foreground">
          Terms
        </Link>{" "}
        and{" "}
        <Link href="/privacy" className="underline hover:text-muted-foreground">
          Privacy Policy
        </Link>
      </p>
    </div>
  );
}
